import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { AppState, IdentityId, Page, Category } from '../types';

const defaultState: AppState = {
  currentPage: 'splash',
  selectedIdentity: null,
  currentSceneIndex: 0,
  selectedSceneId: null,
  selectedCategory: 'all',
};

interface AppContextValue {
  state: AppState;
  navigate: (page: Page) => void;
  selectIdentity: (id: IdentityId) => void;
  selectScene: (id: string) => void;
  setSceneIndex: (index: number) => void;
  setCategory: (category: Category | 'all') => void;
  goBack: () => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AppState>(defaultState);
  const [history, setHistory] = useState<Page[]>(['splash']);

  const navigate = useCallback((page: Page) => {
    setState((prev) => ({ ...prev, currentPage: page }));
    setHistory((prev) => [...prev, page]);
  }, []);

  const selectIdentity = useCallback((id: IdentityId) => {
    setState((prev) => ({ ...prev, selectedIdentity: id, currentSceneIndex: 0 }));
  }, []);

  const selectScene = useCallback((id: string) => {
    setState((prev) => ({ ...prev, selectedSceneId: id }));
  }, []);

  const setSceneIndex = useCallback((index: number) => {
    setState((prev) => ({ ...prev, currentSceneIndex: index }));
  }, []);

  const setCategory = useCallback((category: Category | 'all') => {
    setState((prev) => ({ ...prev, selectedCategory: category }));
  }, []);

  const goBack = useCallback(() => {
    setHistory((prev) => {
      if (prev.length <= 1) return prev;
      const newHistory = prev.slice(0, -1);
      const previousPage = newHistory[newHistory.length - 1];
      setState((s) => ({ ...s, currentPage: previousPage }));
      return newHistory;
    });
  }, []);

  const value: AppContextValue = {
    state,
    navigate,
    selectIdentity,
    selectScene,
    setSceneIndex,
    setCategory,
    goBack,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppState(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppState must be used within AppProvider');
  return context;
}
